import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useCurrency } from '../../hooks/useCurrency';
import { Calendar, Clock, FileText, Hash, List } from 'lucide-react';
import type { RecurringEntry } from '../../types/recurring-entries';
import { RECURRENCE_FREQUENCY_LABELS, RECURRENCE_FREQUENCY_COLORS } from '../../types/recurring-entries';

interface RecurringEntryDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  entry: RecurringEntry | null;
}

export function RecurringEntryDetailModal({ isOpen, onClose, entry }: RecurringEntryDetailModalProps) {
  const { formatConverted } = useCurrency();

  if (!entry) return null;

  const freqLabel = RECURRENCE_FREQUENCY_LABELS[entry.frequency];
  const freqColor = RECURRENCE_FREQUENCY_COLORS[entry.frequency];
  const totalDebit = entry.templateLines.reduce((s, l) => s + l.debit, 0);
  const totalCredit = entry.templateLines.reduce((s, l) => s + l.credit, 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Écriture récurrente — ${entry.name}`} size="lg">
      <div className="space-y-5">
        {/* En-tête */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Nom :</span>
              <span className="font-medium text-foreground">{entry.name}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Fréquence :</span>
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${freqColor.bg} ${freqColor.text}`}>
                {freqLabel}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Prochaine :</span>
              <span className="font-medium text-foreground">
                {new Date(entry.nextRunDate).toLocaleDateString('fr-FR')}
              </span>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Hash className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Statut :</span>
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                entry.isActive
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
              }`}>
                {entry.isActive ? 'Actif' : 'Inactif'}
              </span>
            </div>
            {entry.lastRunDate && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Dernière exéc. :</span>
                <span className="font-medium text-foreground">
                  {new Date(entry.lastRunDate).toLocaleDateString('fr-FR')}
                </span>
              </div>
            )}
            {entry.endDate && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Fin :</span>
                <span className="font-medium text-foreground">
                  {new Date(entry.endDate).toLocaleDateString('fr-FR')}
                </span>
              </div>
            )}
          </div>
        </div>

        {entry.description && (
          <p className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">{entry.description}</p>
        )}

        {/* Lignes modèle */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <List className="h-4 w-4" />
            Lignes modèle ({entry.templateLines.length})
          </h4>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 text-left">
                  <th className="px-3 py-2 font-medium text-muted-foreground">Compte</th>
                  <th className="px-3 py-2 font-medium text-muted-foreground">Libellé</th>
                  <th className="px-3 py-2 font-medium text-muted-foreground text-right">Débit</th>
                  <th className="px-3 py-2 font-medium text-muted-foreground text-right">Crédit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {entry.templateLines.map((line, idx) => (
                  <tr key={idx}>
                    <td className="px-3 py-2">
                      <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                        {line.accountCode || '—'}
                      </span>
                      <span className="ml-2 text-muted-foreground">{line.accountName}</span>
                    </td>
                    <td className="px-3 py-2 text-foreground">{line.description || '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">
                      {line.debit > 0 ? formatConverted(line.debit) : ''}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {line.credit > 0 ? formatConverted(line.credit) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-muted/30 font-semibold">
                  <td className="px-3 py-2" colSpan={2}>Total</td>
                  <td className="px-3 py-2 text-right font-mono">{formatConverted(totalDebit)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatConverted(totalCredit)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t border-border">
          <Button variant="secondary" onClick={onClose}>Fermer</Button>
        </div>
      </div>
    </Modal>
  );
}
